import { useEffect, useState } from 'react';
import { pendingConflicts, pendingMutations } from './db';
import { isNative } from './platform';
import { flushOutbox, SYNC_EVENT } from './sync';

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    let alive = true;
    let removeNative: (() => void) | undefined;

    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    if (isNative()) {
      void import('@capacitor/network').then(async ({ Network }) => {
        if (!alive) return;
        const status = await Network.getStatus();
        setOnline(status.connected);
        const listener = await Network.addListener(
          'networkStatusChange',
          (next) => setOnline(next.connected),
        );
        removeNative = () => void listener.remove();
      });
    }

    return () => {
      alive = false;
      removeNative?.();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return online;
}

export function usePendingChanges(): { pending: number; conflicts: number } {
  const [state, setState] = useState({ pending: 0, conflicts: 0 });

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const [pending, conflicts] = await Promise.all([
        pendingMutations(),
        pendingConflicts(),
      ]);
      if (alive) setState({ pending, conflicts });
    };
    const onSync = () => void refresh();
    void refresh();
    window.addEventListener(SYNC_EVENT, onSync);
    window.addEventListener('online', onSync);
    const timer = window.setInterval(refresh, 15_000);
    return () => {
      alive = false;
      window.removeEventListener(SYNC_EVENT, onSync);
      window.removeEventListener('online', onSync);
      window.clearInterval(timer);
    };
  }, []);

  return state;
}

export function useFlushOutbox(): () => Promise<void> {
  return async () => {
    await flushOutbox();
  };
}
